import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '../components/ui';
import { api } from '../lib/api';

type IntelType = 'infrastructure' | 'zoning' | 'demographic' | 'tenant_movement' | 'market_trend' | 'development' | 'regulatory' | 'broker_note' | 'competitive' | 'other';
type IntelImpact = 'positive' | 'negative' | 'neutral' | 'uncertain';
type ConfidenceLevel = 'high' | 'medium' | 'low' | 'rumor';

interface MarketIntelItem {
  id: string;
  intel_type: IntelType;
  impact: IntelImpact;
  title: string;
  description: string;
  submarket?: string;
  city?: string;
  state?: string;
  effective_date?: string;
  expiration_date?: string;
  source?: string;
  source_url?: string;
  confidence_level: ConfidenceLevel;
  freshness_status: string;
  tags: string[];
  related_property_ids?: string[];
  created_at: string;
  updated_at: string;
}

const INTEL_TYPE_LABELS: Record<IntelType, string> = {
  infrastructure: 'Infrastructure',
  zoning: 'Zoning',
  demographic: 'Demographic',
  tenant_movement: 'Tenant Movement',
  market_trend: 'Market Trend',
  development: 'Development',
  regulatory: 'Regulatory',
  broker_note: 'Broker Note',
  competitive: 'Competitive',
  other: 'Other',
};

const IMPACT_COLORS: Record<IntelImpact, string> = {
  positive: 'bg-success-wash text-success',
  negative: 'bg-red-100 text-red-800',
  neutral: 'bg-gray-100 text-gray-800',
  uncertain: 'bg-warning-wash text-warning',
};

const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  high: 'bg-info-wash text-info',
  medium: 'bg-gray-100 text-gray-700',
  low: 'bg-warning-wash text-warning',
  rumor: 'bg-red-50 text-red-600',
};

const FRESHNESS_COLORS: Record<string, string> = {
  current: 'bg-success-wash text-success',
  needs_review: 'bg-warning-wash text-warning',
  stale: 'bg-red-100 text-red-800',
  unverified: 'bg-gray-100 text-gray-500',
};

export default function MarketIntel() {
  const [intel, setIntel] = useState<MarketIntelItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    intel_type: 'broker_note' as IntelType,
    impact: 'neutral' as IntelImpact,
    title: '',
    description: '',
    submarket: '',
    city: '',
    state: 'CA',
    source: '',
    confidence_level: 'medium' as ConfidenceLevel,
    effective_date: '',
    tags: '',
  });

  const fetchIntel = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (typeFilter) params.intel_type = typeFilter;
      if (cityFilter) params.city = cityFilter;
      if (searchQuery) params.search = searchQuery;
      const data: any = await api.listMarketIntel(params);
      setIntel(data.intel || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load market intel');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, cityFilter, searchQuery]);

  useEffect(() => { fetchIntel(); }, [fetchIntel]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.description) return;
    setSaving(true);
    try {
      await api.createMarketIntel({
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        effective_date: form.effective_date || undefined,
      });
      setShowAdd(false);
      setForm({ intel_type: 'broker_note', impact: 'neutral', title: '', description: '', submarket: '', city: '', state: 'CA', source: '', confidence_level: 'medium', effective_date: '', tags: '' });
      fetchIntel();
    } catch (err: any) {
      setError(err.message || 'Failed to create intel');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await api.verifyMarketIntel(id);
      fetchIntel();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this market intel?')) return;
    try {
      await api.deleteMarketIntel(id);
      fetchIntel();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Market Intelligence</h1>
            <p className="mt-1 text-sm text-gray-500">
              Proprietary insights, broker notes, and market observations — {total} records
            </p>
          </div>
          <Button onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? 'Cancel' : '+ Add Intel'}
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Add Form */}
        {showAdd && (
          <Card>
            <CardHeader><CardTitle>New Market Intel</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Amazon expanding distribution in Fontana" required />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Details about what you learned, impact on market..." required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.intel_type} onChange={e => setForm({...form, intel_type: e.target.value as IntelType})}>
                    {Object.entries(INTEL_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.impact} onChange={e => setForm({...form, impact: e.target.value as IntelImpact})}>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                    <option value="neutral">Neutral</option>
                    <option value="uncertain">Uncertain</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submarket</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.submarket} onChange={e => setForm({...form, submarket: e.target.value})} placeholder="e.g., Inland Empire West" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="e.g., Ontario" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="e.g., City Council Meeting, Broker Tour" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confidence</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.confidence_level} onChange={e => setForm({...form, confidence_level: e.target.value as ConfidenceLevel})}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                    <option value="rumor">Rumor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.effective_date} onChange={e => setForm({...form, effective_date: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="e.g., amazon, logistics, expansion" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-3">
                  <Button type="button" onClick={() => setShowAdd(false)} className="bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</Button>
                  <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Intel'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {Object.entries(INTEL_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Filter by city..." value={cityFilter} onChange={e => setCityFilter(e.target.value)} />
          <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px]" placeholder="Search title or description..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {/* Intel List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading market intelligence...</div>
        ) : intel.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No market intel yet</p>
            <p className="text-gray-400 text-sm mt-2">Start capturing broker notes, zoning changes, and market observations</p>
          </div>
        ) : (
          <div className="space-y-4">
            {intel.map(item => (
              <Card key={item.id}>
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={IMPACT_COLORS[item.impact]}>{item.impact}</Badge>
                        <Badge className="bg-gray-100 text-gray-800">{INTEL_TYPE_LABELS[item.intel_type] || item.intel_type}</Badge>
                        <Badge className={CONFIDENCE_COLORS[item.confidence_level]}>{item.confidence_level}</Badge>
                        <Badge className={FRESHNESS_COLORS[item.freshness_status] || 'bg-gray-100 text-gray-500'}>{item.freshness_status}</Badge>
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">{item.title}</h3>
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{item.description}</p>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-400">
                        {item.submarket && <span>Submarket: {item.submarket}</span>}
                        {item.city && <span>City: {item.city}</span>}
                        {item.source && <span>Source: {item.source}</span>}
                        <span>Added: {new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                      {item.tags?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {item.tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{tag}</span>
                          ))}
                        </div>
                      )}
                      {item.related_property_ids && item.related_property_ids.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          <span className="text-gray-400">Related properties:</span>
                          {item.related_property_ids.map(pid => (
                            <Link key={pid} to={`/data-hub/${pid}`} className="text-primary-500 hover:underline">
                              {pid.slice(0, 8)}...
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => handleVerify(item.id)} className="text-xs px-3 py-1.5 bg-success-wash text-success rounded-lg hover:bg-success-wash transition-colors" title="Verify this intel is still accurate">Verify</button>
                      <button onClick={() => handleDelete(item.id)} className="text-xs px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">Delete</button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
